/**
 * Where the live preview lives. Wide screens: the sticky right-hand column
 * beside the fields. Narrow screens (the editor is one column there, and the
 * preview used to sit thousands of pixels below the field being edited): a
 * sticky "Preview" button that opens it in a bottom sheet (a modal <dialog>,
 * so focus, Esc and the inert page come from the platform).
 *
 * The frame stays mounted while the sheet is closed, so opening it is
 * instant: the widget has already drawn the current draft.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { WidgetPreview } from './WidgetPreview';

/** Must match the one-column breakpoint of `.editor` in styles.css. */
export const NARROW_QUERY = '(max-width: 1180px)';

function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const on = () => setMatches(mql.matches);
    on();
    mql.addEventListener?.('change', on);
    return () => mql.removeEventListener?.('change', on);
  }, [query]);
  return matches;
}

/** The sticky button that opens the sheet; rendered at the top of the fields. */
export function PreviewButton({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="preview-fab-row">
      <button type="button" className="btn btn-sm preview-fab" onClick={onOpen} aria-haspopup="dialog">
        <span aria-hidden="true">◧</span> Preview
      </button>
    </div>
  );
}

export function usePreviewDock() {
  const narrow = useMediaQuery(NARROW_QUERY);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!narrow) setOpen(false);
  }, [narrow]);
  return { narrow, open, setOpen };
}

export function PreviewSheet({ siteKey, open, onClose }: { siteKey: string; open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-label="Widget preview"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // A click on the backdrop (the dialog box itself, outside the panel).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sheet-panel">
        <div className="sheet-head">
          <strong>Preview</strong>
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="sheet-body">
          <WidgetPreview siteKey={siteKey} frameHeight="min(560px, calc(100dvh - 300px))" />
        </div>
      </div>
    </dialog>
  );
}

/**
 * The editor layout: fields on the left, the preview on the right — or, on a
 * narrow screen, fields only with the sheet.
 */
export function EditorWithPreview({ siteKey, children }: { siteKey: string; children: ReactNode }) {
  const { narrow, open, setOpen } = usePreviewDock();
  return (
    <div className="editor">
      <div>
        {narrow ? <PreviewButton onOpen={() => setOpen(true)} /> : null}
        {children}
      </div>
      {narrow ? (
        <PreviewSheet siteKey={siteKey} open={open} onClose={() => setOpen(false)} />
      ) : (
        <div className="preview-col">
          <WidgetPreview siteKey={siteKey} />
        </div>
      )}
    </div>
  );
}
